type PlanRecord = Record<string, unknown>;

export type MealPlanPreferredChoices = {
  appliedAllergies: string[];
  appliedIntolerances: string[];
  appliedCuisineTypes: string[];
  appliedPreferences: string[];
};

const toStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
};

const mergeUnique = (...lists: string[][]): string[] =>
  Array.from(new Set(lists.flat().map((value) => value.trim()).filter(Boolean)));

const asRecord = (value: unknown): PlanRecord => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as PlanRecord;
  }
  return {};
};

/**
 * Plan-setup choices only. Ignores client-profile echoes like `intolerancesApplied`.
 */
const collectFromPlanRecord = (planRecord: PlanRecord): MealPlanPreferredChoices => {
  const searchParamsRecord = asRecord(planRecord.searchParams);
  const overrideClientGoalsRecord = {
    ...asRecord(planRecord.overrideClientGoals),
    ...asRecord(searchParamsRecord.overrideClientGoals),
  };

  return {
    appliedAllergies: mergeUnique(
      toStringList(overrideClientGoalsRecord.allergies),
      toStringList(searchParamsRecord.allergies),
      toStringList(planRecord.allergies),
    ),
    appliedIntolerances: mergeUnique(
      toStringList(overrideClientGoalsRecord.intolerances),
      toStringList(searchParamsRecord.intolerances),
      toStringList(planRecord.intolerances),
    ),
    appliedCuisineTypes: mergeUnique(
      toStringList(overrideClientGoalsRecord.cuisineTypes),
      toStringList(searchParamsRecord.cuisineTypes),
      toStringList(planRecord.cuisineTypes),
      toStringList(planRecord.cuisineTypesApplied),
    ),
    appliedPreferences: mergeUnique(
      toStringList(overrideClientGoalsRecord.preferences),
      toStringList(overrideClientGoalsRecord.dietaryPreferences),
      toStringList(searchParamsRecord.preferences),
      toStringList(planRecord.preferences),
      toStringList(planRecord.dietaryPreferences),
      toStringList(planRecord.dietaryPreferencesApplied),
    ),
  };
};

/** Merges dietary choices stored on one or more meal-plan payloads. */
export const extractMealPlanPreferredChoices = (
  ...planRecords: Array<PlanRecord | null | undefined>
): MealPlanPreferredChoices => {
  const extracted = planRecords
    .filter((record): record is PlanRecord => Boolean(record && typeof record === 'object'))
    .map(collectFromPlanRecord);

  return {
    appliedAllergies: mergeUnique(...extracted.map((item) => item.appliedAllergies)),
    appliedIntolerances: mergeUnique(...extracted.map((item) => item.appliedIntolerances)),
    appliedCuisineTypes: mergeUnique(...extracted.map((item) => item.appliedCuisineTypes)),
    appliedPreferences: mergeUnique(...extracted.map((item) => item.appliedPreferences)),
  };
};
